import axios from 'axios';

// const API_URL = "http://localhost:1337";

let API_URL = "https://staging-strapi.orsai.org";
// Si estoy en desarrollo, usar localhost
// if(process.env.NODE_ENV === 'development') {
// API_URL = "http://localhost:1337";
// }

class StrapiService {
  constructor() {
    this.cache = {
      talleres: {
        data: null,
        timestamp: null
      },
      docentes: {
        data: null,
        timestamp: null
      }
    };
  }

  // Método auxiliar para verificar si el caché es válido
  _isCacheValid(cacheKey) {
    if (!this.cache[cacheKey]?.data || !this.cache[cacheKey]?.timestamp) {
      return false;
    }
    const now = Date.now();
    const cacheAge = now - this.cache[cacheKey].timestamp;
    // Aumentado a 60 segundos para reducir requests duplicados (basado en análisis HAR)
    return cacheAge < 60000; // 60 segundos en milisegundos
  }
  
  // Generar clave de cache basada en endpoint y parámetros
  _getCacheKey(endpoint, params = {}) {
    const paramsStr = Object.keys(params).sort().map(key => `${key}=${params[key]}`).join('&');
    return `${endpoint}${paramsStr ? `?${paramsStr}` : ''}`;
  }

  // Método auxiliar para actualizar el caché
  _updateCache(cacheKey, data) {
    this.cache[cacheKey] = {
      data,
      timestamp: Date.now()
    };
  }

  // Método auxiliar para construir la URL
  _buildUrl(endpoint) {
    return `${API_URL}${endpoint}`;
  }

  // Método auxiliar para obtener los headers con autenticación
  _getHeaders() {
    let token = localStorage.getItem('token');
    if (!token && typeof document !== 'undefined') {
      // Fallback: tomar token desde cookie 'orsai_token'
      const cookieStr = `; ${document.cookie}`;
      const parts = cookieStr.split(`; orsai_token=`);
      if (parts.length === 2) {
        token = parts.pop().split(';').shift();
      }
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Basic ${token}` } : {})
    };
  }

  // Método auxiliar para manejar errores
  _handleError(error) {
    console.error('Error en la llamada a Strapi:', error);
    throw error;
  }

  // GET - Obtener datos
  async get(endpoint, params = {}) {
    try {
      // Verificar cache primero (para cualquier endpoint con parámetros)
      const cacheKey = this._getCacheKey(endpoint, params);
      if (!this.cache[cacheKey]) {
        this.cache[cacheKey] = { data: null, timestamp: null };
      }
      
      if (this._isCacheValid(cacheKey)) {
        console.log(`[STRAPI CACHE] Cache hit: ${endpoint}`);
        return this.cache[cacheKey].data;
      }
      
      // Construir el queryString manualmente (codificar valores para espacios y caracteres especiales)
      const queryParts = [];
      for (const [key, value] of Object.entries(params)) {
        queryParts.push(`${key}=${encodeURIComponent(value)}`);
      }
      const queryString = queryParts.join('&');

      const url = queryString
        ? `${this._buildUrl(endpoint)}?${queryString}`
        : this._buildUrl(endpoint);

      const response = await fetch(url, {
        headers: this._getHeaders()
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('token');
          window.location.href = '/login';
          throw new Error('Sesión expirada');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Actualizar cache
      this._updateCache(cacheKey, data);
      
      return data;
    } catch (error) {
      this._handleError(error);
    }
  }

  // POST - Crear nuevos registros
  async post(endpoint, data) {
    try {
      const response = await fetch(this._buildUrl(endpoint), {
        method: 'POST',
        headers: this._getHeaders(),
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      this._handleError(error);
    }
  }

  // PUT - Actualizar registros existentes
  async put(endpoint, data) {
    try {
      const response = await fetch(this._buildUrl(endpoint), {
        method: 'PUT',
        headers: this._getHeaders(),
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      this._handleError(error);
    }
  }

  // DELETE - Eliminar registros
  async delete(endpoint) {
    try {
      const response = await fetch(this._buildUrl(endpoint), {
        method: 'DELETE',
        headers: this._getHeaders(),
      });
      return await response.json();
    } catch (error) {
      this._handleError(error);
    }
  }

  // Métodos específicos para entidades

  // Cuentos
  async getCuentos(params = {}) {
    return this.get('/api/cuentos', params);
  }

  async getCuentoById(id) {
    return this.get(`/api/cuentos/${id}`);
  }

  async getCuentoWithAlumno(id) {
    return this.get(`/api/cuentos?filters[id]=${id}&populate=alumno`);
  }

  async getCuentosRandom(params = {}) {
    return this.get('/api/cuentos/random-cuentos', params);
  }

  async getCuentosByUsuario(usuarioId) {
    return this.get(`/api/cuentos?filters[idUsuario]=${usuarioId}`);
  }

  async getCuentosByTaller(taller) {
    return this.get(`/api/cuentos?filters[taller]=${taller}&populate=*`);
  }

  async getCuentosVotadosPorUsuario(userId, page = 1, pageSize = 10) {
    return this.get(`/api/cuentos/voted-by-user/${userId}?page=${page}&pageSize=${pageSize}`);
  }

  async getCuentosAudioVotadosPorUsuario(userId, page = 1, pageSize = 10) {
    return this.get(`/api/cuentos/audio-voted-by-user/${userId}?page=${page}&pageSize=${pageSize}`);
  }

  async getCuentosGrupos() {
    return this.get('/api/cuentos?filters[taller]=grupales_thc1&populate=*');
  }

  async getCuentosGlobalStats() {
    return this.get('/api/cuentos/global-stats');
  }

  async getCuentosGlobalStatsAudio() {
    return this.get('/api/cuentos/global-stats-audio');
  }

  async getCuentosPrivateRanking(page = 1, order = 'votos', orderDirection = 'desc') {
    return this.get(`/api/cuentos/private-ranking?page=${page}&orderBy=${order}&orderDirection=${orderDirection}`);
  }

  async getCuentosPrivateRankingAudio(page = 1, order = 'votos', orderDirection = 'desc') {
    return this.get(`/api/cuentos/private-ranking-audio?page=${page}&orderBy=${order}&orderDirection=${orderDirection}`);
  }

  async getCuentosStats(idCuento) {
    return this.get(`/api/cuentos/${idCuento}/stats`);
  }

  async getCuentosStatsAudio(idCuento) {
    return this.get(`/api/cuentos/${idCuento}/stats-audio`);
  }

  // Votos
  async createVoto(data) {
    return this.post('/api/votos', data);
  }

  async getVotosByUsuario(userId) {
    return this.get(`/api/votos?filters[idUsuario]=${userId}&filters[bala]=true`);
  }

  async getVotosByCuento(cuentoId, userId) {
    return this.get(`/api/votos?filters[idCuento]=${cuentoId}&filters[idUsuario]=${userId}`);
  }

  // Votos Audio
  async createVotoAudio(data) {
    return this.post('/api/votos-audios', data);
  }

  async trackTiempoAudio(data) {
    return this.put('/api/tiempo-audios/track', data);
  }

  // Lecturas
  async createLectura(data) {
    return this.post('/api/lecturas', data);
  }

  // Alumnos
  async getAlumnosByUsuario(usuarioId) {
    return this.get(`/api/alumnos?filters[idUsuario]=${usuarioId}`);
  }

  async getAlumnoByUsuarioAndTaller(usuarioEmail, tallerSlug) {
    return this.get('/api/alumnos', {
      'filters[email][$eq]': usuarioEmail,
      'filters[talleres][slug][$eq]': tallerSlug,
      'populate': 'talleres'
    });
  }

  async getCuentoByAlumnoAndTaller(alumnoId, tallerSlug) {
    console.log("AlumnoId", alumnoId);
    console.log("TallerSlug", tallerSlug);
    const cuentos = await this.get('/api/cuentos', {
      'filters[alumno][id]': alumnoId,
      'filters[taller][slug]': tallerSlug,
      'populate': '*',
      'sort[0]': 'createdAt:DESC',
      'pagination[limit]': 1
    });
    return cuentos.data[0];
  }

  async getCuentoById(cuentoId) {
    return this.get(`/api/cuentos/${cuentoId}`, {
      'populate': '*'
    });
  }

  async isAlumno(email) {
    return this.get(`/api/alumnos/es-alumno?email=${email}`);
  }

  // Ranking
  async getRanking(params = {}) {
    return this.get('/api/cuentos/ranking', params);
  }

  // Upload
  async uploadAudio(file) {
    const formData = new FormData();
    formData.append('files', file);
    return this.post('/api/upload', formData);
  }

  // Talleres
  async getTalleres() {
    if (this._isCacheValid('talleres')) {
      return this.cache.talleres.data;
    }
    const data = await this.get('/api/talleres?pagination[pageSize]=100');
    this._updateCache('talleres', data);
    return data;
  }

  /**
   * Buscar talleres por texto (titulo o slug). Para autocompletado.
   * Si query está vacío devuelve los primeros 50.
   * @param {string} query - Texto para buscar por título o slug
   * @param {object} options - Opciones: { createdAfter: 'YYYY-MM-DD' } para filtrar por fecha de creación
   */
  async getTalleresBusqueda(query, options = {}) {
    const q = (query || '').trim()
    const baseParams = { 'pagination[pageSize]': 50 }
    if (options.createdAfter) {
      baseParams['filters[createdAt][$gte]'] = options.createdAfter
    }
    if (!q) {
      return this.get('/api/talleres', baseParams)
    }
    return this.get('/api/talleres', {
      ...baseParams,
      'filters[$or][0][titulo][$containsi]': q,
      'filters[$or][1][slug][$containsi]': q
    })
  }

  // Talleres del alumno
  async getTalleresByUsuario(usuarioId, email) {
    if (!email) {
      console.warn('No se proporcionó email para buscar talleres');
      return [];
    }

    // Siempre usar email para obtener los talleres con todos los campos necesarios
    const res = await this.get(`/api/alumnos?filters[email]=${email}&populate[]=talleres.agenda&populate[]=talleres.imagenPortada`);
    return res.data[0]?.talleres || [];
  }

  async getComprasShopify(email, sku) {
    try {
      const compra = await this.get(`/api/taller/compras-shopify?email=${email}&sku=${sku}`);

      if (compra && compra.cantidad < 1) {
        return null;
      }
      return compra;
    } catch (error) {
      this._handleError(error);
    }
  }

  async verificarUsuario(email) {
    try {
      const response = await this.get(`/api/usuarios?filters[email]=${email}`);
      const usuarios = response.data.data;
      return {
        id: usuarios && usuarios.length > 0 ? usuarios[0].id : null
      };
    } catch (error) {
      console.error('Error al verificar usuario:', error);
      throw error;
    }
  }

  async registrarAlumno(alumnoData) {
    try {
      // Crear alumno sin el campo 'taller' (la asignación se hace vía relación 'talleres')
      // Este endpoint espera datos en plano (no dentro de { data })
      const { nombre, apellido, email, correoCompra, idUsuario } = alumnoData;
      const response = await this.post('/api/alumnos', {
        nombre,
        apellido,
        email,
        correoCompra,
        idUsuario
      });

      return response.data;
    } catch (error) {
      console.error('Error al registrar alumno:', error);
      throw error;
    }
  }

  // Actualizar datos del alumno
  async actualizarAlumno(alumnoId, data) {
    try {
      const response = await this.put(`/api/alumnos/${alumnoId}`, {
        data
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar alumno:', error);
      throw error;
    }
  }

  async getTallerBySku(sku) {
    try {
      const response = await this.get(`/api/talleres?filters[sku]=${encodeURIComponent(sku)}`);
      const data = response?.data ?? response?.results ?? response;
      const list = Array.isArray(data) ? data : [];
      return list.length > 0 ? list[0] : null;
    } catch (error) {
      console.error('Error al buscar taller por SKU:', error);
      throw error;
    }
  }

  async asignarTallerAlumno(alumnoId, tallerId) {
    try {
      const response = await this.put(`/api/alumnos/${alumnoId}`, {
        data: {
          talleres: {
            connect: [tallerId]
          }
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error al asignar taller al alumno:', error);
      throw error;
    }
  }
  async getAlumnoByEmail(email) {
    console.log(email);
    return this.get(`/api/alumnos?filters[email]=${encodeURIComponent(email)}`);
  }

  /**
   * Obtiene todos los alumnos de un taller por su slug, ordenados por apellido y nombre.
   * Equivalente al listado: alumno_id, nombre, apellido, email.
   */
  async getAlumnosByTallerSlug(slug) {
    const response = await this.get('/api/alumnos', {
      'filters[talleres][slug][$eq]': slug,
      'sort[0]': 'apellido',
      'sort[1]': 'nombre',
      'pagination[pageSize]': 500
    });
    return response;
  }

  // Buscar un voto específico
  async buscarVoto(alumnoId, cuentoId, tipoVoto = 'resumen') {
    try {
      const response = await this.get('/api/votos/buscar', {
        params: {
          alumnoId,
          cuentoId,
          tipoVoto
        }
      });
      return response;
    } catch (error) {
      console.error('Error al buscar voto:', error);
      throw error;
    }
  }

  // Obtener todos los votos de un alumno
  async obtenerVotosAlumno(alumnoId, tipoVoto = 'resumen', tallerId = null) {
    try {
      const response = await this.get(`/api/votos/alumno?alumnoId=${alumnoId}&tipoVoto=${tipoVoto}&tallerId=${tallerId}`);
      console.log(response);
      return response;
    } catch (error) {
      console.error('Error al obtener votos del alumno:', error);
      throw error;
    }
  }

  // Nuevos
  // Ranking
  // /api/statistics/ranking-votos/:taller-marzo/:cuento
  async getRankingCuentos(slug, votacion) {
    console.log(slug, votacion);
    return await this.get(`/api/statistics/ranking-votos/${slug}/${votacion}`);
  }

  async getCuentosStatsNuevas(idCuento) {
    return await this.get(`/api/statistics/cuento/${idCuento}`);
  }

  async getTallerBySlug(slug) {
    const talleres = await this.get(`/api/talleres?filters[slug]=${slug}&populate=*`);
    const taller = await this.get(`/api/talleres/${talleres[0].documentId}?populate=*`);
    return taller;
  }

  // async getRankingCuentos(taller, tipo) {
  //   return this.get(`/api/statistics/ranking/${taller}?tipo=${tipo}`);
  // }

  async getRankingCuentosGlobal(taller) {
    return this.get(`/api/statistics/ranking-votos/${taller}/global`);
  }

  // Obtener total de alumnos por taller
  async getTotalAlumnosPorTaller(tallerId) {
    try {
      const response = await this.get('/api/alumnos/count', {
        'filters[talleres][id][$eq]': tallerId
      });
      return response;
    } catch (error) {
      console.error('Error al obtener total de alumnos:', error);
      return 0;
    }
  }

  // Obtener total de anécdotas por taller
  async getTotalAnecdotasPorTaller(tallerId) {
    try {
      const response = await this.get('/api/cuentos/count', {
        'filters[taller][id][$eq]': tallerId
      });
      return response;
    } catch (error) {
      console.error('Error al obtener total de anécdotas:', error);
      return 0;
    }
  }

  // Docentes
  async getDocentes() {
    if (this._isCacheValid('docentes')) {
      return this.cache.docentes.data;
    }
    const data = await this.get('/api/docentes?populate=*');
    this._updateCache('docentes', data);
    return data;
  }

  async getDocenteById(id) {
    return this.get(`/api/docentes/${id}?populate=*`);
  }

}

// Exportar una instancia única del servicio
const strapiService = new StrapiService();
export default strapiService;